import { isValidCpf, isValidCnpj } from './document'

export const REQUIRED_PASSWORD_MESSAGE = 'Campo obrigatório. Informe sua senha.'
export const SHORT_PASSWORD_MESSAGE = 'Senha muito curta. Use 8 ou mais caracteres.'
export const NUMBER_SEQUENCE_PASSWORD_MESSAGE = 'Não use números em sequência.'
export const LETTERS_SEQUENCE_PASSWORD_MESSAGE = 'Não use letras em sequência.'
export const KEYBOARD_SEQUENCE_PASSWORD_MESSAGE = 'Não use sequências do teclado.'
export const VERY_WEAK_PASSWORD_MESSAGE = 'Senha muito fraca. Experimente usar letras e números.'
export const WEAK_PASSWORD_MESSAGE = 'Senha fraca. Tente usar maiúsculo, minúsculo e símbolos.'
export const GOOD_PASSWORD_MESSAGE = 'Senha ok. Experimente colocar mais caracteres.'
export const STRONG_PASSWORD_MESSAGE = 'Senha forte. Excelente!'

export const isSequenceOfNumbers = pattern =>
  pattern.test('0123456789') || pattern.test('9876543210')

export const isSequenceOfLetters = pattern =>
  pattern.test('abcdefghijklmnopqrstuvwxyz') || pattern.test('zyxwvutsrqponmlkjihgfedcba')

export const isSequenceOfKeys = pattern =>
  pattern.test('qwertyuiop') ||
  pattern.test('asdfghjkl') ||
  pattern.test('zxcvbnm') ||
  pattern.test('poiuytrewq') ||
  pattern.test('lkjhgfdsa') ||
  pattern.test('mnbvcxz')

export const hasMinimumCharacteres = value => value.length >= 8

export const hasNumbers = value => /\d/.test(value)

export const hasLowerCase = value => /[a-z]/.test(value)

export const hasUpperCase = value => /[A-Z]/.test(value)

export const hasSymbols = value => /[^a-z\d]/i.test(value)

const countMatch = password =>
  [
    hasNumbers(password),
    hasLowerCase(password),
    hasUpperCase(password),
    hasSymbols(password),
  ].filter(Boolean).length

const isVeryWeakPassword = password => countMatch(password) <= 1

const isWeakPassword = password => countMatch(password) <= 3

const isGoodPassword = password => countMatch(password) >= 3 && password.length < 12

const groupLength = 4
const safeRegex = item => item.replace(/(.)/g, '[$1]')
const fakePattern = { test: () => false }

export const createPattern = pass => {
  try {
    if (pass.length < groupLength) {
      return fakePattern
    }
    const len = pass.length - (groupLength - 1)
    const prePattern = Array(len)
      .fill(pass)
      .map((item, key) => item.slice(key, key + groupLength))
      .map(safeRegex)
      .join('|')
    return new RegExp(`(${prePattern})`, 'i')
  } catch (error) {
    return fakePattern
  }
}

const rules = [
  {
    isTruth: test => test.password.length === 0,
    strength: -3,
    message: REQUIRED_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => !hasMinimumCharacteres(test.password),
    strength: -2,
    message: SHORT_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfNumbers(test.pattern),
    strength: -1,
    message: NUMBER_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfLetters(test.pattern),
    strength: -1,
    message: LETTERS_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfKeys(test.pattern),
    strength: -1,
    message: KEYBOARD_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isVeryWeakPassword(test.password),
    strength: 1,
    message: VERY_WEAK_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isWeakPassword(test.password),
    strength: 2,
    message: WEAK_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isGoodPassword(test.password),
    strength: 3,
    message: GOOD_PASSWORD_MESSAGE,
  },
]
const rulesVariant = [
  {
    isTruth: test => test.password.length === 0,
    strength: 0,
    message: REQUIRED_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => !hasMinimumCharacteres(test.password),
    strength: 1,
    message: SHORT_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfNumbers(test.pattern),
    strength: 1,
    message: NUMBER_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfLetters(test.pattern),
    strength: 1,
    message: LETTERS_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isSequenceOfKeys(test.pattern),
    strength: 1,
    message: KEYBOARD_SEQUENCE_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isVeryWeakPassword(test.password),
    strength: 1,
    message: VERY_WEAK_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isWeakPassword(test.password),
    strength: 2,
    message: WEAK_PASSWORD_MESSAGE,
  },
  {
    isTruth: test => isGoodPassword(test.password),
    strength: 3,
    message: GOOD_PASSWORD_MESSAGE,
  },
]

const findFirstCase = (password, pattern) => {
  return rulesVariant.reduce(
    (result, item) => (result === null && item.isTruth({ password, pattern }) ? item : result),
    null
  )
}

export const checkStrength = password => {
  const defaultResult = {
    strength: 4,
    message: STRONG_PASSWORD_MESSAGE,
  }
  const pattern = createPattern(password)
  const result = findFirstCase(password, pattern)
  return result === null ? defaultResult : result
}

const passwordRules = [
  {
    hasError: test =>
      !hasMinimumCharacteres(test.password) ||
      isSequenceOfNumbers(test.pattern) ||
      isSequenceOfLetters(test.pattern) ||
      isSequenceOfKeys(test.pattern),
    message: '8 ou mais caracteres (não sequenciais)',
  },
  {
    hasError: test => !hasLowerCase(test.password) || !hasUpperCase(test.password),
    message: 'Letras maiúsculas e minúsculas.',
  },
  {
    hasError: test => !hasNumbers(test.password),
    message: 'Pelo menos um número',
  },
  {
    hasError: test => !hasSymbols(test.password),
    message: 'Pelo menos um caracter especial (exemplo: @ ! $ & #)',
  },
]
export const checkPasswordRules = password => {
  const pattern = createPattern(password)
  return passwordRules.map(rule => ({
    ...rule,
    hasError: rule.hasError({ password, pattern }),
  }))
}

export const isValidEmail = emailParam => {
  const email = emailParam.toLowerCase().trim()
  return (
    !!email &&
    typeof email.match === 'function' &&
    !!email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,7})$/) // eslint-disable-line
  )
}

export const isValidIdentifier = value => {
  if (!value?.trim()) return false
  const trimmed = value.trim()
  return isValidEmail(trimmed) || isValidCpf(trimmed) || isValidCnpj(trimmed)
}

export const maxNumbers = (value, n) => {
  const numbers = value.match(/\d+/g)
  return numbers && numbers.length ? numbers.join('').length <= n : true
}

export const isValidNickname = value => {
  return value.length >= 3 && maxNumbers(value, 5)
}

export const isValidFullName = value => {
  const names = value.toString().trim().split(' ')

  return !/\d+/.test(value) && names.length > 1 && !names.some(x => x.length < 2)
}

export const isValidateDate = dateString => {
  const date = new Date(dateString)
  const currentDate = new Date()

  if (isNaN(date.getTime()) || date > currentDate) {
    return false
  }

  const year = date.getFullYear()
  const minYear = 1900
  const maxYear = currentDate.getFullYear()

  if (year < minYear || year > maxYear) {
    return false
  }

  return true
}
